import { useEffect, useMemo, useState } from 'react'
import styles from './style.module.scss'
import { useTranslation } from 'react-i18next'

import Btn from '../../../../UI/Btn'
import MiniHeader from '../../../../UI/MiniHeader'
import Search from '../../../../UI/Search'
import Table from '../../../../UI/Table'
import FilePreview from '../../../../UI/FilePreview'
import businessPlanIcon from '../../../../assets/icons/businessPlan.svg'
import paperParentalIcon from '../../../../assets/icons/paperParental.svg'
import reportIcon from '../../../../assets/icons/reportIcon.svg'
import { TApi } from '../../../../shared/const'
import { toast } from 'react-toastify'
import http from '../../../../shared/http'
import FilterPanel from '../../../../UI/FilterPanel'
import useFilters from '../../../../UI/FilterPanel/useFilters'
import { companyField, documentTypeField } from '../../../../UI/FilterPanel/fields'
import BtnFilter from '../../../../UI/BtnFilter'
import Pagination from '../../../../UI/Pagination'
import { downloadFile, documentOwnerName, localizedDocumentTypeLabel } from '../../../../shared/helpers'
import { notifyError } from '../../../../shared/errorMessage'
import useDebouncedValue from '../../../../shared/useDebouncedValue'
import useLatestOnly from '../../../../shared/useLatestOnly'

const documentTypesIcon = {
	BUSINESS_PLAN: businessPlanIcon,
	PARTICIPATION_DOCUMENT: businessPlanIcon,
	PARENTAL_CONFIRMATION: paperParentalIcon,
	CONSENT_DOCUMENT: businessPlanIcon,
	COMPANY_CERTIFICATE: businessPlanIcon,
	SHAREHOLDER_CERTIFICATE: businessPlanIcon,
	SHARE_TEMPLATE: businessPlanIcon,
	COURSE_CERTIFICATE: paperParentalIcon,
	REPORT: reportIcon
}

// Mehr Dateien packt der Server nicht in ein Archiv (services/documents/export).
const ZIP_MAX_FILES = 1500

function DocumentsList(props) {
	const { t } = useTranslation()
	const [documents, setDocuments] = useState([])
	const [countDocuments, setCountDocuments] = useState(0)
	const [page, setPage] = useState(0)
	const limit = 10
	// eslint-disable-next-line no-unused-vars
	const [company, setCompany] = useState({})
	const [sort, setSort] = useState({ name: 'name', direction: 'asc' })
	const [search, setSearch] = useState('')
	const [showFilter, setShowFilter] = useState(false)
	// Position der Datei in der Vorschau; -1 heisst: keine Vorschau offen.
	const [previewIndex, setPreviewIndex] = useState(-1)
	// Welche Ausgabe gerade entsteht ('excel' oder 'zip'); null heisst: keine.
	const [exporting, setExporting] = useState(null)

	const download = name => {
		http.get(TApi.ADMIN_DOWNLOAD_DOCUMENT(name), {
			headers: {
				'Content-Type': 'application/pdf'
			},
			responseType: 'blob'
		}).then(({ data }) => {
			const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
			const link = document.createElement('a')
			link.href = url
			link.setAttribute('download', name)
			document.body.appendChild(link)
			link.click()
			link.parentNode.removeChild(link)
		})
	}

	// Rechnungen haben eine eigene Ansicht, hier waeren sie doppelt.
	const fields = useMemo(() => [documentTypeField(t, { withInvoice: false }), companyField(t)], [t])

	const filters = useFilters({ storageKey: 'documents-admin', fields })
	const filterQuery = JSON.stringify(filters.query)
	const debouncedSearch = useDebouncedValue(search)
	const latest = useLatestOnly()

	const fetchDocuments = () => {
		const isCurrent = latest()

		http.get(TApi.ADMIN_GET_LIST_DOCUMENTS, {
			params: {
				limit,
				offset: page * limit,
				direction: sort.direction,
				order: sort.name,
				id: company.value ? company.value : null,
				search: debouncedSearch,
				filter: filterQuery
			}
		}).then(({ data }) => {
			if (!isCurrent()) return

			// Neue Liste, alte Position: Die Vorschau zeigt sonst die Datei, die
			// an dieser Stelle vorher stand.
			setPreviewIndex(-1)
			setDocuments(
				data.rows.map(e => {
					const date = new Date(e.createdAt)
					return {
						name: e.originalName,
						file: e.name,
						type: e.type,
						time:
							('0' + date.getDate()).slice(-2) +
							'/' +
							('0' + (date.getMonth() + 1)).slice(-2) +
							'/' +
							date.getFullYear(),
						company: e.Company ? e.Company.name : '',
						owner: documentOwnerName(e.Creator)
					}
				})
			)
			setCountDocuments(data.count)
		})
	}

	// Geblättert wird durch die Liste, die gerade zu sehen ist — dieselbe
	// Reihenfolge, dieselbe Seite. Weiter als bis zum Seitenende geht es nicht;
	// wer mehr am Stück durchsehen will, stellt die Seitenlänge hoch.
	const previewItems = useMemo(
		() =>
			documents.map(item => ({
				name: item.name,
				url: TApi.ADMIN_DOWNLOAD_DOCUMENT(item.file),
				file: item.file,
				meta: [localizedDocumentTypeLabel(item.type), item.company, item.owner, item.time]
					.filter(Boolean)
					.join(' · ')
			})),
		[documents]
	)

	/*
	 * Die Auswahl der Liste zum Mitnehmen (Anforderung #195): als Excel-Tabelle
	 * mit Person, Schule, Junior Company und Lehrkraft, oder die Dateien selbst
	 * als ZIP. Beides nimmt Suche und Filter, die gerade gelten — mit „Art des
	 * Dokuments: Kursnachweis" sind das genau die Kursurkunden.
	 */
	const exportieren = art => {
		if (art === 'zip' && countDocuments > ZIP_MAX_FILES) {
			toast.info(t('documentsZipTooMany', { count: countDocuments, max: ZIP_MAX_FILES }))
			return
		}

		setExporting(art)
		http.get(art === 'zip' ? TApi.ADMIN_DOCUMENTS_EXPORT_ZIP : TApi.ADMIN_DOCUMENTS_EXPORT_EXCEL, {
			params: { search: debouncedSearch, filter: filterQuery },
			responseType: 'blob',
			// Bei einem Blob steckt die Begründung des Servers im Blob selbst —
			// sie wird unten ausgepackt und dann gemeldet, nicht vorher.
			disableToast: true
		})
			.then(({ data }) => {
				const tag = new Date().toISOString().slice(0, 10)
				downloadFile(`Dokumente-${tag}.${art === 'zip' ? 'zip' : 'xlsx'}`, data)
			})
			.catch(async error => {
				const data = error?.response?.data
				if (data instanceof Blob) {
					try {
						error.response.data = JSON.parse(await data.text())
					} catch (e) {
						error.response.data = {}
					}
				}
				notifyError(error, 'documentsExportFailed')
			})
			.finally(() => setExporting(null))
	}

	const handleSort = (name, direction) => {
		setSort({ name, direction })
	}
	const handleFilterChange = (key, selected) => {
		setPage(0)
		filters.setField(key, selected)
	}

	const handleFilterReset = () => {
		setPage(0)
		filters.resetAll()
	}

	const handleSearchChange = value => {
		setPage(0)
		setSearch(value)
	}

	// Gefiltert und gesucht wird ohne Knopfdruck; jede Aenderung setzt die Seite
	// zurueck, sonst stuende man auf Seite 4 einer kuerzer gewordenen Liste.
	useEffect(() => {
		fetchDocuments()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, company, sort, debouncedSearch, filterQuery])

	// Gesucht wird hier nach dem Unternehmen, nicht nach der Dateinummer —
	// deshalb steht das Unternehmen vorne und der Dateiname am Ende. Der Name
	// bleibt der Name des Datensatzes und damit die hervorgehobene Spalte
	// (`primary`), und der Herunterladen-Knopf wandert mit ihm ans Zeilenende.
	const columns = useMemo(
		() => [
			{
				Header: () => {
					return <span className={styles.tableTitleCompany}>{t('Company')}</span>
				},
				minWidth: 170,
				accessor: 'company',
				Cell: ({ row }) => {
					return <p className={styles.tableCompany}>{row.values.company}</p>
				}
			},
			{
				Header: t('Type'),
				minWidth: 170,
				accessor: 'type',
				Cell: ({ row }) => {
					return <p className={styles.tableType}>{localizedDocumentTypeLabel(row.values.type)}</p>
				}
			},
			/*
			 * Wem das Dokument gehoert. Leer, wo es niemandem zuzuordnen ist —
			 * Urkunden und Rechnungen gehoeren der Junior Company, nicht einer
			 * Person (siehe shared/helpers, documentOwnerName).
			 */
			{
				Header: t('Assigned to'),
				minWidth: 150,
				accessor: 'owner',
				Cell: ({ row }) => (
					<p className={styles.tableOwner}>{row.values.owner || <span aria-hidden="true">—</span>}</p>
				)
			},
			{
				Header: t('Date'),
				width: 80,
				accessor: 'time',
				Cell: ({ row }) => {
					return <p className={styles.tableDate}>{row.values.time}</p>
				}
			},
			{
				Header: t('Document name'),
				minWidth: 170,
				accessor: 'name',
				primary: true,
				Cell: ({ row }) => {
					return (
						<div className={styles.tableNameContainer}>
							<img
								className={styles.tableDocumentIcon}
								src={documentTypesIcon[row.values.type]}
								alt="icon"
							/>
							<span className={styles.tableName}>{row.values.name}</span>
							<Btn
								className={styles.tableDownload}
								theme="download"
								icon="download"
								iconSize={{ w: 24, h: 24 }}
								onClick={() => download(row.original.file)}
							/>
						</div>
					)
				}
			}
		],
		[t]
	)
	return (
		<div className={styles.root}>
			<MiniHeader className={styles.headerContainer} title={t('documents')}>
				<div className={styles.header}>
					<div className={styles.searchContainer}>
						<Search
							value={search}
							onChange={e => handleSearchChange(e.target.value)}
							onSubmit={e => e.preventDefault()}
							placeholder={t('Search for documents...')}
						/>
						<BtnFilter
							icon="arrow"
							onClick={() => setShowFilter(!showFilter)}
							count={filters.count}
						>
							{t('Filter')} ({filters.count})
						</BtnFilter>
					</div>
				</div>
			</MiniHeader>
			<div className={styles.subHeader}>
				<Pagination
					limit={limit}
					page={page}
					label={t('documents')}
					countAll={countDocuments}
					onChange={setPage}
				/>
				<div className={styles.downloadContent}>
					{t('Download selection as')}
					<div className={styles.downloadButtons}>
						<Btn
							theme="download"
							iconSize={{ w: 24, h: 24 }}
							icon="download"
							iconColor="#22404D"
							loading={exporting === 'excel'}
							disabled={!countDocuments || Boolean(exporting)}
							onClick={() => exportieren('excel')}
						>
							Excel
						</Btn>
						<Btn
							theme="download"
							iconSize={{ w: 24, h: 24 }}
							icon="download"
							iconColor="#22404D"
							loading={exporting === 'zip'}
							disabled={!countDocuments || Boolean(exporting)}
							onClick={() => exportieren('zip')}
						>
							{t('Files (ZIP)')}
						</Btn>
					</div>
				</div>
			</div>

			<div className="container">
				<Table
					columns={columns}
					handleSort={handleSort}
					items={documents}
					limit={limit}
					countAll={countDocuments}
					onChange={setPage}
					page={page}
					paginationLabel={t('documents')}
					onRowClick={(item, row) => setPreviewIndex(row.index)}
					rowTitle={t('Preview')}
				/>
			</div>
			{previewIndex >= 0 && (
				<FilePreview
					items={previewItems}
					index={previewIndex}
					onIndexChange={setPreviewIndex}
					onClose={() => setPreviewIndex(-1)}
					onDownload={item => download(item.file)}
				/>
			)}
			<FilterPanel
				open={showFilter}
				onClose={() => setShowFilter(false)}
				fields={fields}
				values={filters.values}
				count={filters.count}
				onChange={handleFilterChange}
				onResetAll={handleFilterReset}
			/>
		</div>
	)
}

export default DocumentsList
